import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";

export const useRouteTabSelection = ({
  defaultTab,
  enabled = true,
  parameterKey = "tab",
  resolvePathname,
  route,
  tabs,
}: {
  defaultTab: string;
  enabled?: boolean;
  parameterKey?: string;
  resolvePathname?: (tabName: string) => string;
  route: string;
  tabs: string[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTabName = searchParams.get(parameterKey);
  const requestedTabIndex = tabs.findIndex(
    (tabName) => tabName === requestedTabName,
  );
  const defaultTabIndex = tabs.findIndex((tabName) => tabName === defaultTab);
  const selectedTabIndex =
    requestedTabIndex >= 0 ? requestedTabIndex : Math.max(defaultTabIndex, 0);
  const selectedTabName = tabs[selectedTabIndex] ?? defaultTab;
  const hasInitialisedRoute = useRef(false);

  useEffect(() => {
    if (!enabled || hasInitialisedRoute.current) return;

    hasInitialisedRoute.current = true;
    if (requestedTabName === selectedTabName) return;

    setSearchParams(
      (currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.set(parameterKey, selectedTabName);
        return nextSearchParams;
      },
      { replace: true },
    );
  }, [
    enabled,
    parameterKey,
    requestedTabName,
    selectedTabName,
    setSearchParams,
  ]);

  const getTabUrl = useCallback(
    (nextTabName: string) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set(parameterKey, nextTabName);

      return {
        pathname: resolvePathname?.(nextTabName) ?? route,
        search: nextSearchParams.toString(),
      };
    },
    [parameterKey, resolvePathname, route, searchParams],
  );

  return {
    selectedTabIndex,
    selectedTabName,
    getTabUrl,
  };
};
